import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { IUserLookupService } from "../../lookup/model/IUserLookupService";
import { ISystemConfigService } from "../../profile/ISystemConfigService";
import { TranslateService } from "@ngx-translate/core";

import { PricingService } from "../../lookup/pricing/PricingService";
import { ITraderGroupLookupService } from "../../lookup/model/ITraderGroupLookupService";
import { SysFeatures } from "../core/SysFeatures";
import { StrategyTooltip } from "../../adapter/orderBlotter/StrategyTooltip";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { OrderDataLockLogger } from "shared/opt-in-logger";
import { OrderDataModelUpdateSource } from "../../model/orders/OrderDataModelUpdateSource";
import { TSLLabelHelper } from "shared/cubiq-services/model/orders/TSLLabelHelper";

import { LoggerService } from "@bfx/logger";
import {
  BmarkBaseRiskDisplayModel,
  BmarkSpotRiskDisplayModel,
} from "../benchmarks/BmarkSpotRiskDisplay.model";
const logger = LoggerService.for(__dirname);

export const MODIFY_LOCK_IGNORE_STATES: Array<CubiqTS.OrderState> = [
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
];
export const FILL_NOW_LOCK_IGNORE_STATES: Array<CubiqTS.OrderState> = [
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
];

const NPV_WARNING_TEXT: string = "Warning: Missing NPV amounts";

export class StatusIconsHelper {
  private localeDateTimeString: string = "DD-MMM-YY HH:mm:ss";
  private localeDateString: string = "DD-MMM-YY";

  protected showUserName: boolean;
  protected stratgyTooltip: StrategyTooltip;
  protected showNPV: boolean = false;

  public showOnlyStrategyIcons: boolean = false;

  private tslAnimation: Map<
    string,
    {
      a: boolean;
      evtMsgId: string;
    }
  > = new Map();

  /**
   *
   * @param userLookupService
   * @param sysConfig
   * @param translate
   * @param tslLabelHelper Knows the current blotter modes defined to get correct price and pip labels
   * @param traderGroupLookup
   * @param sysConfigUtil
   */
  constructor(
    protected userLookupService: IUserLookupService,
    sysConfig: ISystemConfigService,
    protected translate: TranslateService,
    protected pricingService: PricingService,
    protected tslLabelHelper: TSLLabelHelper,
    private traderGroupLookup: ITraderGroupLookupService,
    private sysConfigUtil?: BfxSysConfigUtilService,
  ) {
    this.stratgyTooltip = new StrategyTooltip(translate, pricingService, sysConfigUtil);
    sysConfig.getSystemConfig(SysFeatures.displayUserNames).then((value: string) => {
      this.showUserName =
        value == "true" || value == "TRUE" || value == "1" || value == "Y" || value == "y";
    });
    sysConfig.getSystemConfig(SysFeatures.bmNPV).then((value: string) => {
      this.showNPV =
        value == "true" || value == "TRUE" || value == "1" || value == "Y" || value == "y";
    });
  }

  // adapt(column: ColumnDefinition, row: any, ...modelObjects: any[]): void {
  //     let order: CubiqTS.OrderDataImpl = modelObjects[0];
  //     let iconStr = this.retrieveData(order);
  //     row[column.data] = iconStr;

  //     // as we will need the value in menu building an launcher for pauseResume window
  //     row['pauseState'] = order.pauseState;
  // }

  getIcons(order: CubiqTS.OrderDataImpl | BmarkSpotRiskDisplayModel): string {
    if (order instanceof CubiqTS.OrderDataImpl) {
      let iconStr: string;
      if (this.showOnlyStrategyIcons) {
        iconStr = this.getCol2Icon(order);
      } else if (order.orderType == CubiqTS.OrderType.BMALGO) {
        iconStr = this.getCol1Icon(order);
        iconStr += this.getCol3Icon(order);
        iconStr += this.getCol4Icon(order);
        iconStr += this.getCol5Icon(order);
      } else if (
        order.orderType == CubiqTS.OrderType.BENCHMARK &&
        order.fixingDetails?.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder
      ) {
        // Handle FF icons for BMHO
        iconStr = this.getCol1Icon(order);
      } else {
        iconStr = this.getCol1Icon(order);
        iconStr += this.getCol2Icon(order);
        iconStr += this.getCol3Icon(order);
        iconStr += this.getCol4Icon(order);
        iconStr += this.getCol5Icon(order);
        iconStr += this.getCol6Icon(order);
      }
      return iconStr;
    } else if (order instanceof BmarkSpotRiskDisplayModel) {
      return this.getNPVIconForSpotRisk(order);
    }

    return "";
  }

  getClientBMIcons(order: CubiqTS.OrderDataImpl): string {
    let iconStr: string = "";
    if (order instanceof CubiqTS.OrderDataImpl) {
      iconStr += this.getCol1Icon(order);
      iconStr += this.getCol2Icon(order);
    }
    return iconStr;
  }

  getInterventionIcons(interventionData: CubiqTS.InterventionEvent) {
    if (
      CubiqTS.InterventionEventType.FILL === interventionData.eventType &&
      CubiqTS.TransferStatus.FAILED === interventionData.transferStatus
    ) {
      return this.getInterventionAutoStealFailedIcon(interventionData);
      /*} else if(interventionData.orderData instanceof CubiqTS.OrderDataImpl){
          // TODO switch transfer icon to use interventionData.targetTraderGroupId ?
          return this.getCol1Icon(interventionData.orderData);*/
    } else {
      return this.getInterventionLockIcon(interventionData);
    }
  }

  /**
   * Lock / Transfer /Forward First
   * @param order
   * @returns
   */
  public getCol1Icon(order: CubiqTS.OrderDataImpl): string {
    if (order instanceof CubiqTS.OrderDataImpl) {
      let colStr: string = this.getLockIcon(order);
      colStr += this.getTransferIcon(order);

      if (order.orderType == CubiqTS.OrderType.BENCHMARK) {
        colStr += this.getForwardFirstIcon(order);
      }

      if (colStr == "") {
        return '<i class="icon icon-spacer"></i>';
      }

      return colStr;
    }

    return "";
  }

  /**
   * Strategy Icon
   * @param order
   * @returns
   */
  private getCol2Icon(order: CubiqTS.OrderDataImpl): string {
    let colStr: string;
    colStr = this.getStrategyIcon(order);

    if (order.orderType == CubiqTS.OrderType.BENCHMARK) {
      let npvIcon: string = this.getNPVIcon(order);

      if (npvIcon != null) {
        colStr += npvIcon;
      }
    }

    if (colStr == "") {
      return '<i class="icon icon-spacer"></i>';
    }

    return colStr;
  }

  /**
   * Pause Icon
   * @param order
   * @returns
   */
  private getCol3Icon(order: CubiqTS.OrderDataImpl): string {
    let colStr: string = this.getPauseIcon(order);
    if (colStr == "") {
      return '<i class="icon icon-spacer"></i>';
    }
    return colStr;
  }

  /**
   * activating / expiring / suspending / suspended
   *
   * @param order
   * @returns
   */
  private getCol4Icon(order: CubiqTS.OrderDataImpl): string {
    let colStr: string = this.getActivatingIcon(order);
    if (colStr == "") colStr += this.getExpiringIcon(order);
    if (colStr == "") colStr += this.getSuspensionIcon(order);
    if (colStr == "") {
      return '<i class="icon icon-spacer"></i>';
    }
    return colStr;
  }

  getPauseIcon(order: CubiqTS.OrderDataImpl): string {
    let ret: string = "";
    if (order.pauseState === CubiqTS.PauseState.PAUSED) {
      let tip: string = this.translate.instant("tooltip.PAUSE");
      ret =
        '<i class="icon icon_ion_pause_circle_filled" style="font-size:15px" alt="' +
        tip +
        '" title="' +
        tip +
        '"></i>';
    }
    return ret;
  }

  /**
   *  trailing active or inactive / Inflights checks
   * @param order
   * @returns
   */
  private getCol5Icon(order: CubiqTS.OrderDataImpl): string {
    let colStr: string = this.getInflighCheckStatusIcon(order);
    if (colStr == "") {
      colStr = this.getTrailingIcon(order);
      if (colStr == "") {
        return '<i class="icon icon-spacer"></i>';
      }
    }
    return colStr;
  }

  /**
   * Credit Check (DI)
   * @param order
   * @returns
   */
  private getCol6Icon(order: CubiqTS.OrderDataImpl): string {
    let colStr: string = this.getCreditCheckFailIcon(order);
    if (colStr == "") {
      return '<i class="icon icon-spacer"></i>';
    }
    return colStr;
  }

  // Forward First icon (FF)
  private getForwardFirstIcon(order: CubiqTS.OrderDataImpl): string {
    if (order.reqQuoteId != null) {
      return `<span class="badge-icon bfx-badge-info-bold">FF</span>`;
    }
    return "";
  }

  // NPV Warning icon
  private getNPVIcon(order: CubiqTS.OrderDataImpl): string {
    if (this.showNPV) {
      try {
        if (
          order?.allocationData != null &&
          Array.isArray(order.allocationData) &&
          !order.allocationData.every((a) => a.npvAllocationQty != null && a.npvSettleQty != null)
        ) {
          return `<i class="icon icon-error" title="${NPV_WARNING_TEXT}" alt="${NPV_WARNING_TEXT}"></i>`;
        }
      } catch (err) {
        logger.out.error(
          `${this.constructor.name}: Error while assessing NPV presence: ${err}`,
          err,
        );
      }
    }
    return "";
  }

  // NPV Warning icon
  public getNPVIconForSpotRisk(spotRisk: BmarkSpotRiskDisplayModel): string {
    if (this.showNPV) {
      try {
        if (spotRisk.hasNPVWarning()) {
          return `<i class="icon icon-error adjusted" title="${NPV_WARNING_TEXT}" alt="${NPV_WARNING_TEXT}"></i>`;
        }
      } catch (err) {
        logger.out.error(
          `${this.constructor.name}: Error while assessing NPV presence: ${err}`,
          err,
        );
      }
    }
    return "";
  }

  /**
   * We have a numberof variants Icons
   * a) Has no Siblings
   * [1] "Gamma" - has at least one gamma child
   * [2] "mkt kid" - no sibling - but has at leats one child relationship which contains a MKT order
   * [3] "kid" - no sibling - but has at leats one child relationship - and no MKT kids
   * [4] "simple" - no siblings & no kids
   *
   * b) Has Siblings
   * [5] "loop" loop sibling (can't have kids anyway)
   * [6] "oco" oco sibling but neither of em have kids
   * [7] "mkt complex" Sibling and at leats one of em has a child relationship - which contains a MKT order
   * [8] "complex" Sibling and at leats one of em has a child relationship - and no MKT kids
   * [9] "sibling" Sibling and no kids
   *
   * @param order
   */
  public getStrategyIcon(order: CubiqTS.OrderDataImpl, inclDesc: boolean = true): string {
    let hasSiblings: boolean = false;
    let isLoop: boolean = false;
    let isOCO: boolean = false;
    let hasKids: boolean = false;
    let hasGammaKids: boolean = false;
    let hasMktKids: boolean = false;

    if (order.siblingRelationship && order.siblingRelationship.relationshipType) {
      switch (order.siblingRelationship.relationshipType) {
        case CubiqTS.OrderRelationshipType.OCO: {
          hasSiblings = isOCO = true;
          break;
        }
        case CubiqTS.OrderRelationshipType.LOOP: {
          hasSiblings = isLoop = true;
          break;
        }
        case CubiqTS.OrderRelationshipType.GAMMA: {
          hasSiblings = hasGammaKids = true;
          break;
        }
        default: {
          hasSiblings = false;
          break;
        }
      }
    }

    if (null != order.childRelationships && order.childRelationships.length > 0) {
      for (const childRelationship of order.childRelationships) {
        if (this.isMarketOrder(childRelationship.order)) {
          hasMktKids = true;
        }

        switch (childRelationship.relationshipType) {
          case CubiqTS.OrderRelationshipType.GAMMA: {
            hasGammaKids = true;
            break;
          }
          case CubiqTS.OrderRelationshipType.LOOP: {
            isLoop = true; // seems daft but was in code I refactored
            break;
          }
          case CubiqTS.OrderRelationshipType.IFDONE:
          case CubiqTS.OrderRelationshipType.ON_TIMEOUT: {
            hasKids = true;
            break;
          }
        }
      }
    }

    let cssClasses: string = null;

    if (hasSiblings) {
      // b) Has Siblings

      if (isLoop) {
        // [5] "loop" loop sibling (can't have kids anyway)
        cssClasses = "icon-bfx_loop";
      } else if (isOCO && !hasKids) {
        // [6] "oco" oco sibling but neither of em have kids
        cssClasses = "icon-bfx_oco highlight-strategy-ico";
      } else if (hasMktKids) {
        // [7] "mkt complex" Sibling and at leats one of em has a child relationship - which contains a MKT order
        cssClasses = "icon-bfx_complex highlight-strategy-ico";
      } else if (hasKids) {
        // [8] "complex" Sibling and at leats one of em has a child relationship - and no MKT kids
        cssClasses = "icon-bfx_complex highlight-strategy-ico";
      } else if (hasGammaKids) {
        // [1] "Gamma" - has at least one gamma child
        cssClasses = "icon-ladder highlight-strategy-ico";
      }
      {
        // [9] "sibling" Sibling and no kids
        // shouldn't get here
        // so not an icon
      }
    } else {
      // a) Has no Siblings

      if (hasGammaKids) {
        // [1] "Gamma" - has at least one gamma child
        cssClasses = "icon-ladder highlight-strategy-ico";
      } else if (hasMktKids) {
        // [2] "mkt kid" - no sibling - but has at leats one child relationship which contains a MKT order
        cssClasses = "icon-bfx_done_mkt";
      } else if (hasKids) {
        // [3] "kid" - no sibling - but has at leats one child relationship - and no MKT kids
        cssClasses = "icon-bfx_done";
      } else {
        // [4] "simple" - no siblings & no kids
        // so not an icon
      }
    }

    let htmlForIcon: string = "";
    if (null != cssClasses) {
      htmlForIcon = htmlForIcon + '<i class="icon ' + cssClasses + '"';
      if (inclDesc) {
        let desc: string = this.stratgyTooltip.getStrategyDescription(order);
        htmlForIcon = htmlForIcon + ' alt="' + desc + '" title="' + desc + '"';
      }
      htmlForIcon = htmlForIcon + "></i>";
    }

    return htmlForIcon;
  }

  isMarketOrder(order: CubiqTS.OrderDataImpl) {
    let ret = false;
    if (null != order && null != order.orderType) {
      ret =
        CubiqTS.OrderType.MARKET == order.orderType ||
        CubiqTS.OrderType.MARKET_FOK == order.orderType ||
        CubiqTS.OrderType.MARKET_IOC == order.orderType;
    }
    return ret;
  }

  private getTransferIcon(order: CubiqTS.OrderDataImpl): string {
    if (order instanceof CubiqTS.OrderDataImpl && CubiqTS.Long.isLong(order.targetDeskId)) {
      let zero = CubiqTS.Long.ZERO;

      if (zero.comp(order.targetDeskId) < 0) {
        let tip: string;
        let tg = this.traderGroupLookup.getTraderGroup(order.targetDeskId);
        if (tg) tip = tg.groupCode;
        // if(tgIsExternallyFilled){
        //     show the steal icons instead
        //     we dont have a steal icon at the moment
        //     return `<i class="icon icon-bfx_arrow_right"></i>`;
        // }
        return '<i class="icon icon-bfx_arrow_right" alt="' + tip + '" title="' + tip + '"></i>';
      }
    }
    return "";
  }

  private getLockIcon(order: CubiqTS.OrderDataImpl): string {
    if (
      order instanceof CubiqTS.OrderDataImpl &&
      order.lockType != null &&
      (order.lockType != CubiqTS.LockType.MODIFY_LOCK ||
        !MODIFY_LOCK_IGNORE_STATES.includes(order.orderState)) &&
      (order.lockType != CubiqTS.LockType.FILL_NOW_LOCK ||
        !FILL_NOW_LOCK_IGNORE_STATES.includes(order.orderState))
    ) {
      let user = this.userLookupService.getUserById(order.lockUserId.toString());
      let userStr = "";
      if (user) userStr = this.showUserName ? user.userName : user.userCode;
      let resourceID: string = "tooltip.LOCKED_BY";
      if (order.lockType == CubiqTS.LockType.MODIFY_LOCK) {
        resourceID = "tooltip.LOCKED_FOR_MOD";
      } else if (order.lockType == CubiqTS.LockType.FILL_LOCK) {
        resourceID = "tooltip.LOCKED_FOR_FILL";
      } else if (order.lockType == CubiqTS.LockType.FILL_NOW_LOCK) {
        resourceID = "tooltip.LOCKED_FOR_FILL_NOW";
      } else if (order.lockType == CubiqTS.LockType.CANCEL_LOCK) {
        resourceID = "tooltip.LOCKED_FOR_CANCEL";
      } else if (order.lockType == CubiqTS.LockType.PAUSE_LOCK) {
        resourceID = "tooltip.LOCKED_FOR_PAUSE";
      }
      let tip = this.translate.instant(resourceID, [userStr]);
      return '<i class="icon icon_ion_lock_filled" alt="' + tip + '" title="' + tip + '"></i>';
    }
    OrderDataLockLogger(order, `${this.constructor.name}.getLockIcon()`);
    return "";
  }

  // private getFpeIcon(order: CubiqTS.OrderDataImpl): string {
  //   let fwdPtsStreamOff: boolean = false;
  //   if (fwdPtsStreamOff) {
  //     let tip: string = this.translate.instant("tooltip.FPE");
  //     return (
  //       '<i class="icon icon-small-stale-stream-off" alt="' + tip + '" title="' + tip + '"></i>'
  //     );
  //   }
  //   return "";
  // }
  private getActivatingIcon(order: CubiqTS.OrderDataImpl): string {
    if (order.isAboutToActivate || CubiqTS.OrderState.PENDING === order.orderState) {
      if (CubiqTS.OrderState.SUSPENDED === order.orderState) {
        return this.getSuspensionIcon(order);
      }
      let activation: string = "";
      // As required in https://www.bcdfx.com/ijira/browse/OMS-23840 alias should be replaced with date and time stamp.
      // if(order.activationAlias && order.activationAlias != "CUSTOM"){
      //     activation = order.activationAlias;
      // }
      if (order.activationDts) {
        activation = order.activationDts.format(this.localeDateTimeString);
      }
      let tip: string = this.translate.instant("tooltip.ACTIVATES_AT", [activation]);
      if (order.isAboutToActivate) {
        if (this.shouldFlash(order.activationDts)) {
          return (
            '<i class="icon icon-bfx_order_activating flashIcon" alt="' +
            tip +
            '" title="' +
            tip +
            '"></i>'
          );
        }
        return (
          '<i class="icon icon-bfx_order_activating" alt="' + tip + '" title="' + tip + '"></i>'
        );
      } else if (order.orderState == CubiqTS.OrderState.PENDING) {
        return (
          '<i class="icon icon_ion_schedule_filled" alt="' + tip + '" title="' + tip + '"></i>'
        );
      }
    }
    return "";
  }

  private shouldFlash(moment: CubiqTS.Moment): boolean {
    if (moment) {
      let now = CubiqTS.moment(new Date());
      if (moment.diff(now) <= 60000) {
        return true;
      }
    }
    return false;
  }

  private getExpiringIcon(order: CubiqTS.OrderDataImpl): string {
    if (
      order.isAboutToExpire ||
      order.orderState == CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED ||
      order.orderState == CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED
    ) {
      // this should flash
      let expiry: string = "";
      if (order.expiryAlias && order.expiryAlias != "CUSTOM" && order.expiryDts) {
        // OMS-7654 Changing the way Good Until is displayed, for instances where same alias
        // has different expiryDate
        const aliasPart: string = order.expiryAlias;
        // Get the UTC date
        let datePart: string = order.expiryDts.format(this.localeDateString);
        let aliasAndDate: string = aliasPart + " " + datePart;
        expiry = aliasAndDate;
      } else if (order.expiryDts) {
        expiry = order.expiryDts.format(this.localeDateTimeString);
      }
      let tip = this.translate.instant("tooltip.EXPIRES_AT", [expiry]);
      if (this.shouldFlash(order.expiryDts)) {
        return (
          '<i class="icon icon-bfx_order_expiring flashIcon" alt="' +
          tip +
          '" title="' +
          tip +
          '"></i>'
        );
      }
      return '<i class="icon icon-bfx_order_expiring" alt="' + tip + '" title="' + tip + '"></i>';
    }
    return "";
  }
  private getSuspensionIcon(order: CubiqTS.OrderDataImpl): string {
    let tip: string;
    if (order.isAboutToSuspend || order.orderState == CubiqTS.OrderState.SUSPENDED) {
      let suspension: CubiqTS.SuspensionDetailsImpl =
        order.suspensionDetails as CubiqTS.SuspensionDetailsImpl;
      let from: string = suspension.suspensionFromDts.format(this.localeDateTimeString);
      let to: string = suspension.suspensionToDts.format(this.localeDateTimeString);
      if (order.isAboutToSuspend) {
        tip = this.translate.instant("tooltip.SUSPENDING", [from, to]);
        if (this.shouldFlash(suspension.suspensionFromDts)) {
          return (
            '<i class="icon icon-bfx_suspending flashIcon" alt="' +
            tip +
            '" title="' +
            tip +
            '"></i>'
          );
        }
        return '<i class="icon icon-bfx_suspending" alt="' + tip + '" title="' + tip + '"></i>';
      } else if (order.orderState == CubiqTS.OrderState.SUSPENDED && order.isAboutToActivate) {
        tip = this.translate.instant("tooltip.SUSPENDED", [from, to]);
        if (this.shouldFlash(suspension.suspensionToDts)) {
          return (
            '<i class="icon icon_ion_pause_circle_filled flashIcon" style="font-size: 15px" alt="' +
            tip +
            '" title="' +
            tip +
            '"></i>'
          );
        }
        return (
          '<i class="icon icon_ion_pause_circle_filled" style="font-size: 15px" alt="' +
          tip +
          '" title="' +
          tip +
          '"></i>'
        );
      } else if (order.orderState == CubiqTS.OrderState.SUSPENDED) {
        tip = this.translate.instant("tooltip.SUSPENDED", [from, to]);
        return (
          '<i class="icon icon_ion_pause_circle_filled" style="font-size: 15px" alt="' +
          tip +
          '" title="' +
          tip +
          '"></i>'
        );
      }
    }
    return "";
  }

  private getTrailingIcon(order: CubiqTS.OrderDataImpl): string {
    const icon = (tip: string, classname: "active" | "inactive" | "triggered") => {
      let orderId = order.orderId.toString();
      let animationAB = ``;

      if (classname === "active") {
        const last = this.tslAnimation.get(orderId);

        if (
          Object.hasOwn(order, "source") &&
          Object.hasOwn(order, "evtMsgId") &&
          OrderDataModelUpdateSource.TrailingStopEvent === order["source"]
        ) {
          if (last != null && last.evtMsgId != order["evtMsgId"]) {
            this.tslAnimation.set(orderId, { evtMsgId: order["evtMsgId"], a: !last.a });
            animationAB = last.a ? ` a` : ` b`;
          } else if (last == null) {
            this.tslAnimation.set(orderId, { evtMsgId: order["evtMsgId"], a: false });
            animationAB = ` a`;
          }
        }
      } else {
        this.tslAnimation.delete(orderId);
      }

      return `<i class="badge-icon trailing-stop trailing-stop-${classname}${animationAB}" title="
        ${tip}
        "><data alt="
        ${tip}
        " title="
        ${tip}
        ">TS</data></i>`;
    };

    if (
      this.tslLabelHelper &&
      (order.requestedOrderType == CubiqTS.RequestOrderType.TSL ||
        order.requestedOrderType == CubiqTS.RequestOrderType.TSL_ASK ||
        order.requestedOrderType == CubiqTS.RequestOrderType.TSL_BID) &&
      order.trailingStopDetails instanceof CubiqTS.TrailingStopDetailsImpl
    ) {
      let ts: CubiqTS.TrailingStopDetailsImpl = order.trailingStopDetails;

      if (ts.isTrackingActive) {
        return icon(
          this.translate.instant("tooltip.TRAILING_ACTIVE", [
            this.tslLabelHelper.getTrailingPipsLabel(order),
          ]),
          "active",
        );
      } else if (
        CubiqTS.OrderState.TRIGGERED === order.orderState &&
        ts.origTrackingRt != null &&
        ts.origTrackingRt.isEqualTo(order.requestRt)
      ) {
        return icon(this.translate.instant("order.state.TRIGGERED"), "inactive");
      } else if (CubiqTS.OrderState.TRIGGERED === order.orderState) {
        return icon(this.translate.instant("order.state.TRIGGERED"), "triggered");
      } else {
        return icon(
          this.translate.instant("tooltip.TRAILING_INACTIVE", [
            this.tslLabelHelper.getTrailingPipsLabel(order),
            this.tslLabelHelper.getTrailingTriggerLabel(order),
          ]),
          "inactive",
        );
      }
    }
    return "";
  }

  private getCreditCheckFailIcon(order: CubiqTS.OrderDataImpl): string {
    if (
      order.creditRevalDetails instanceof CubiqTS.CreditRevalDetailsImpl &&
      order.creditRevalDetails != null &&
      order.creditRevalDetails != undefined &&
      (order.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus >= 0
    ) {
      if (
        CubiqTS.CreditStatus.CREDIT_FAILED ===
          (order.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus ||
        CubiqTS.CreditStatus.CREDIT_TIMEDOUT ===
          (order.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus
      ) {
        let tip: string = this.translate.instant("tooltip.CREDIT_CHECK_FAIL");
        return '<i class="badge-icon bfx-badge-negative-moderate" title="' + tip + '">DI</i>';
      }
    }
    return "";
  }

  public getInterventionAutoStealFailedIcon(
    interventionData: CubiqTS.InterventionEventImpl,
  ): string {
    if (
      interventionData instanceof CubiqTS.InterventionEventImpl &&
      CubiqTS.InterventionEventType.FILL === interventionData.eventType &&
      CubiqTS.TransferStatus.FAILED === interventionData.transferStatus
    ) {
      let resourceID: string = "tooltip.AUTO_STEAL_FAILED";
      let tip = this.translate.instant(resourceID);
      return (
        '<i class="icon auto-steal-failed icon_ion_error_filled" alt="' +
        tip +
        '" title="' +
        tip +
        '"></i>'
      );
    }
    return "";
  }

  /*  Added for intervention table fixing rate event lock (manual pricing)*/
  public getInterventionLockIcon(interventionData: CubiqTS.InterventionEventImpl): string {
    if (
      interventionData instanceof CubiqTS.InterventionEventImpl &&
      interventionData.lockUserId != null
    ) {
      let user = this.userLookupService.getUserById(interventionData.lockUserId.toString());
      let resourceID: string = "tooltip.LOCKED_BY";
      let userStr = "another user";
      if (user instanceof CubiqTS.UserImpl) {
        userStr = this.showUserName ? user.userName : user.userCode;
      }

      let tip = this.translate.instant(resourceID, [userStr]);
      return '<i class="icon icon_ion_lock_filled" alt="' + tip + '" title="' + tip + '"></i>';
    }
    return "";
  }

  private getInflighCheckStatusIcon(order: CubiqTS.OrderDataImpl): string {
    if (order.inflightInclusionState === CubiqTS.InflightInclusionState.DISABLED) {
      let tip: string = this.translate.instant("tooltip.INFLIGHT_DISABLED");
      return (
        '<i class="icon icon-bfx_inflight_check_disabled" title="' +
        tip +
        '"><data alt="' +
        tip +
        '" title="' +
        tip +
        '"></data></i>'
      );
    }
    return "";
  }
}
